import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { 
  selectIsAuthenticated,
  selectUser,
  selectIsLoading,
  selectIsFetchingUser,
  selectAuthError,
  selectCurrentToken
} from '../../../store/selectors'
import { 
  loginUser,
  logoutUser,
  fetchUserData,
  clearError,
  forceLogout
} from '../../../store/slices/authSlice'
import { navigateToLogin } from './core/NavigationHelper'
import { refreshDebouncer } from '../../utils/refreshDebouncer'

export const useReduxAuth = () => {
  const dispatch = useAppDispatch()
  
  // Selectors - using individual selectors to avoid re-render issues
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const user = useAppSelector(selectUser)
  const isLoading = useAppSelector(selectIsLoading)
  const isFetchingUser = useAppSelector(selectIsFetchingUser)
  const error = useAppSelector(selectAuthError)
  const currentToken = useAppSelector(selectCurrentToken)
  
  // Compute auth status directly without using selector
  const authStatus = {
    isAuthenticated,
    isLoading,
    isFetchingUser,
    hasUser: !!user,
    isReady: isAuthenticated && !isFetchingUser && !!user
  }
  
  // Actions
  const login = useCallback(async (credentials: { email: string; password: string }) => {
    return dispatch(loginUser(credentials)).unwrap()
  }, [dispatch])
  
  const logout = useCallback(() => {
    dispatch(logoutUser())
  }, [dispatch])
  
  const refreshUserData = useCallback((skipDebounce = false) => {
    if (currentToken) {
      // Use the debouncer to prevent rapid calls, but allow skipDebounce for manual calls
      if (skipDebounce || refreshDebouncer.shouldAllowRefresh('refreshUserData', 3000)) {
        dispatch(fetchUserData({ skipDebounce }))
      } else {
        console.log('Skipping refreshUserData call - debounced to prevent refresh loops')
      }
    }
  }, [dispatch, currentToken])
  
  const clearAuthError = useCallback(() => {
    dispatch(clearError())
  }, [dispatch])
   const forceLogoutAction = useCallback(() => {
    dispatch(forceLogout())
    setTimeout(() => {
      navigateToLogin()
    }, 100)
  }, [dispatch])

  return {
    // State
    isAuthenticated,
    user,
    currentUser: user, // Alias for compatibility
    isLoading,
    isFetchingUser,
    error,
    currentToken,
    authStatus,
    
    // Actions
    login,
    logout,
    refreshUserData,
    clearAuthError,
    forceLogout: forceLogoutAction
  }
}
